// Browser entry for the site publisher (szzt-spec.md §14). This is DOM + OAuth glue —
// typecheck-only, never unit-tested — mirroring @szzt/frontend's browser.ts convention. All the
// load-bearing logic it drives is pure and already unit-tested elsewhere: the publish-flow FSM
// (`reduce`/`canPublish`), the deterministic build (`@szzt/core` build()), the publish write path
// (`@szzt/publish` publish()), and the drag-drop/zip ingest (`@szzt/frontend/browser`).
import { Agent } from "@atproto/api";
import { BrowserOAuthClient } from "@atproto/oauth-client-browser";
import { build, siteOriginLabel } from "@szzt/core";
import type { BuildResult, FileInput, MaslDocument } from "@szzt/core";
import {
  AlreadyMigratedError,
  atpClient,
  atpRecordLister,
  COLLECTION,
  ipfsBlockProducer,
  kuboBlockPut,
  legacyDeploymentsForSite,
  listLegacyRepo,
  manifestBlobCids,
  migrate,
  planMigration,
  prune,
  publish,
  rollback,
  r2BucketPut,
  raslBucketProducer,
  writeRoute,
  type MirrorProducer,
} from "@szzt/publish";
import {
  canPublish,
  initialFlow,
  reduce,
  type FlowEvent,
  type FlowState,
} from "@szzt/frontend";
import {
  createPreview,
  filesFromDataTransfer,
  filesFromFileList,
  mountPreview,
  unzipToFileInputs,
} from "@szzt/frontend/browser";
import { resolveTarget, validateRouteRecord, type FetchedRecord, type RepoReader } from "@szzt/records";
import {
  avatarFromProfile,
  deriveInitialName,
  exportUrl,
  formatSize,
  normalizeMirrorHost,
  parseIpfsConfig,
  parseR2Config,
  parseSessionHint,
  profileEndpointUrl,
  serializeIpfsConfig,
  serializeR2Config,
  serializeSessionHint,
  statusLabel,
  totalResourceBytes,
  type IpfsConfig,
  type R2Config,
  type SessionHint,
} from "./derive.ts";
import { attachHandleTypeahead } from "./typeahead.ts";
import { namedSiteUrl, preferredVisitUrl, ROUTE_COLLECTION, siteNames } from "./names.ts";
import { SESSION_ENDED_NOTICE, sessionLifecycle } from "./session-lifecycle.ts";
import {
  clampKeep,
  planKeepLastPrune,
  previewKeepLast,
  retentionSummary,
  type RetentionVersion,
} from "./retention.ts";
import {
  normalizeSlug,
  serviceDidFor,
  slugInputError,
  slugOutcomeError,
  slugSiteUrl,
  SLUG_ALLOC_LXM,
  SLUG_ALLOC_PATH,
} from "./slug.ts";

// The gateway serves the hosted OAuth client metadata + this app same-origin (see apps/gateway). The
// client_id IS the metadata document's URL, so it's derived from wherever this page is served.
const CLIENT_ID = new URL("/client-metadata.json", location.origin).href;
// Public appview used only to resolve a bare handle -> DID (via DNS/well-known); a full PDS URL input
// skips it entirely. Handle resolution is network-agnostic, so bsky.social resolves any atproto handle.
const HANDLE_RESOLVER = "https://bsky.social";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing #${id}`);
  return node as T;
}

const ui = {
  signedOut: el<HTMLDivElement>("signed-out"),
  signedIn: el<HTMLDivElement>("signed-in"),
  signinForm: el<HTMLFormElement>("signin-form"),
  identifier: el<HTMLInputElement>("identifier"),
  suggestions: el<HTMLUListElement>("suggestions"),
  lastAccount: el<HTMLDivElement>("last-account"),
  continueAs: el<HTMLButtonElement>("continue-as"),
  forgetAccount: el<HTMLButtonElement>("forget-account"),
  continueAvatar: el<HTMLImageElement>("continue-avatar"),
  continueHandle: el<HTMLSpanElement>("continue-handle"),
  signout: el<HTMLButtonElement>("signout"),
  authNotice: el<HTMLParagraphElement>("auth-notice"),
  authLabel: el<HTMLSpanElement>("auth-label"),
  whoText: el<HTMLSpanElement>("who-text"),
  avatar: el<HTMLImageElement>("avatar"),
  identity: el<HTMLSpanElement>("identity"),
  drop: el<HTMLDivElement>("drop"),
  file: el<HTMLInputElement>("file"),
  nameField: el<HTMLDivElement>("name-field"),
  siteName: el<HTMLInputElement>("site-name"),
  fileList: el<HTMLDivElement>("file-list"),
  fileListCount: el<HTMLSpanElement>("file-list-count"),
  fileListItems: el<HTMLUListElement>("file-list-items"),
  clearFiles: el<HTMLButtonElement>("clear-files"),
  preview: el<HTMLElement>("preview"),
  previewFrame: el<HTMLIFrameElement>("preview-frame"),
  status: el<HTMLParagraphElement>("status"),
  publish: el<HTMLButtonElement>("publish"),
  updateBanner: el<HTMLDivElement>("update-banner"),
  updateName: el<HTMLSpanElement>("update-name"),
  cancelUpdate: el<HTMLButtonElement>("cancel-update"),
  versionHistory: el<HTMLDivElement>("version-history"),
  versionList: el<HTMLUListElement>("version-list"),
  retention: el<HTMLDivElement>("retention"),
  retentionKeep: el<HTMLInputElement>("retention-keep"),
  retentionApply: el<HTMLButtonElement>("retention-apply"),
  retentionHint: el<HTMLParagraphElement>("retention-hint"),
  sitesSection: el<HTMLElement>("sites-section"),
  sitesNotice: el<HTMLParagraphElement>("sites-notice"),
  sites: el<HTMLUListElement>("sites"),
  r2Section: el<HTMLElement>("r2-config"),
  r2Account: el<HTMLInputElement>("r2-account"),
  r2Bucket: el<HTMLInputElement>("r2-bucket"),
  r2Key: el<HTMLInputElement>("r2-key"),
  r2Secret: el<HTMLInputElement>("r2-secret"),
  r2Host: el<HTMLInputElement>("r2-host"),
  r2Save: el<HTMLButtonElement>("r2-save"),
  r2Clear: el<HTMLButtonElement>("r2-clear"),
  r2Status: el<HTMLParagraphElement>("r2-status"),
  ipfsSection: el<HTMLElement>("ipfs-config"),
  ipfsApi: el<HTMLInputElement>("ipfs-api"),
  ipfsGateway: el<HTMLInputElement>("ipfs-gateway"),
  ipfsAuth: el<HTMLInputElement>("ipfs-auth"),
  ipfsSave: el<HTMLButtonElement>("ipfs-save"),
  ipfsClear: el<HTMLButtonElement>("ipfs-clear"),
  ipfsStatus: el<HTMLParagraphElement>("ipfs-status"),
};

/** One published `app.szzt.site` pointer record, as listed for the signed-in account. */
interface SiteEntry {
  rkey: string;
  title: string;
  /** AT-URI of the site's active `app.szzt.deployment` (`current.uri`), used to mark the live
   *  version in the version-history panel. */
  currentDeploymentUri?: string;
  /** Total content bytes, filled in lazily by hydrateSizes() after the list first renders. */
  sizeBytes?: number;
  /** A gateway slug claimed for this site this session (§11.1). Set after a successful claim so the
   *  short-name link shows in the row; not persisted — a loadSites() refresh drops it (the pointer
   *  record doesn't store the slug, and there's no site→slug reverse lookup), but the slug still serves. */
  slug?: string;
  /** Publisher-scoped route names, persisted in the configured route collection. */
  names?: string[];
}

// localStorage key for the optimistic-login hint (see parseSessionHint). NOT the session itself — only
// a did/handle remembered so a returning user sees the signed-in shell immediately on reload instead of
// the signed-out form flashing during the async OAuth restore.
const HINT_KEY = "szzt.session-hint";

function readHint(): SessionHint | undefined {
  try {
    return parseSessionHint(localStorage.getItem(HINT_KEY));
  } catch {
    return undefined; // localStorage can be unavailable (private mode / disabled) — just skip the hint
  }
}

function writeHint(did: string, handleValue?: string, avatarValue?: string): void {
  try {
    localStorage.setItem(HINT_KEY, serializeSessionHint(did, handleValue, avatarValue));
  } catch {
    // ignore — a missing hint only costs the optimistic UI on the next load
  }
}

function clearHint(): void {
  try {
    localStorage.removeItem(HINT_KEY);
  } catch {
    // ignore
  }
}

// localStorage key for the last-used account (see `lastAccount`). Persistent across an expired session,
// so it uses its own key rather than the optimistic session hint's.
const LAST_ACCOUNT_KEY = "szzt.last-account";

function readLastAccount(): SessionHint | undefined {
  try {
    return parseSessionHint(localStorage.getItem(LAST_ACCOUNT_KEY));
  } catch {
    return undefined; // localStorage unavailable → simply no one-click affordance
  }
}

function writeLastAccount(did: string, handleValue?: string, avatarValue?: string): void {
  try {
    localStorage.setItem(LAST_ACCOUNT_KEY, serializeSessionHint(did, handleValue, avatarValue));
  } catch {
    // ignore — a missing record only costs the one-click re-login affordance on the next load
  }
}

function clearLastAccount(): void {
  try {
    localStorage.removeItem(LAST_ACCOUNT_KEY);
  } catch {
    // ignore
  }
}

// localStorage key for the optional external-mirror (BYO CDN) config (see parseR2Config). Device-local,
// not account-scoped — a publishing setting for this browser, so it persists across sign-out. Holds R2
// credentials; nothing sensitive is ever logged.
const R2_KEY = "szzt.r2-config";

function readR2Config(): R2Config | undefined {
  try {
    return parseR2Config(localStorage.getItem(R2_KEY));
  } catch {
    return undefined; // localStorage unavailable → simply no mirror
  }
}

function writeR2Config(cfg: R2Config): void {
  try {
    localStorage.setItem(R2_KEY, serializeR2Config(cfg));
  } catch {
    // ignore — a mirror that can't be persisted just won't survive a reload
  }
}

function clearR2Config(): void {
  try {
    localStorage.removeItem(R2_KEY);
  } catch {
    // ignore
  }
}

// localStorage key for the optional external IPFS-mirror config (see parseIpfsConfig). Device-local,
// same discipline as the R2 config: a publishing setting for this browser, persists across sign-out,
// holds a possibly-sensitive RPC Authorization header that is never logged.
const IPFS_KEY = "szzt.ipfs-config";

function readIpfsConfig(): IpfsConfig | undefined {
  try {
    return parseIpfsConfig(localStorage.getItem(IPFS_KEY));
  } catch {
    return undefined; // localStorage unavailable → simply no mirror
  }
}

function writeIpfsConfig(cfg: IpfsConfig): void {
  try {
    localStorage.setItem(IPFS_KEY, serializeIpfsConfig(cfg));
  } catch {
    // ignore — a mirror that can't be persisted just won't survive a reload
  }
}

function clearIpfsConfig(): void {
  try {
    localStorage.removeItem(IPFS_KEY);
  } catch {
    // ignore
  }
}

let state: FlowState = initialFlow;
// The live credential — read by loadSites, siteUrl, doPublish, hydrateSizes, the security gate, etc.
// This is NOT part of AuthState: the auth shell's optimistic display and the real write-authorizing
// credential are deliberately separate. `agent` is assigned ONLY by authDispatch (RESTORED/SIGN_OUT/SESSION_LOST).
let agent: Agent | undefined;

/** Everything known about who to SHOW in the auth shell. Accretes did→handle→avatar and NEVER discards a
 *  field once known. `did` is optional only because a first-time typed handle is known before its DID is
 *  resolved — not because a DID is ever erased. */
interface Identity {
  did?: string;
  handle?: string;
  avatar?: string;
}

type AuthPhase = "signedOut" | "restoring" | "signedIn";

/** The single source of truth for the auth shell. render() is a pure function of this plus `agent` (the
 *  credential). `restoring` = the optimistic shell painted from a known identity while the real OAuth
 *  session settles; `signedIn` requires a live `agent`. See authDispatch for the enforced invariant. */
interface AuthState {
  /** Which shell to show. signedOut = form; restoring = optimistic signed-in shell (no Sign out yet);
   *  signedIn = real live session. */
  phase: AuthPhase;
  /** Who to display; the identity text renders "" only when nothing at all is known. */
  identity: Identity;
  /** The last-used account, kept across an EXPIRED session (and an explicit sign-out) so the signed-out
   *  screen can offer one-click "Continue as" re-login. Cleared only by FORGET_ACCOUNT. */
  lastAccount?: Identity | undefined;
  /** Persistent auth recovery message, separate from publishing/listing notices. */
  notice?: string | undefined;
}

/** The auth transitions, all routed through authDispatch — the one owner of `auth` and `agent`. */
type AuthEvent =
  | { type: "INIT" }
  | { type: "SIGN_IN_INTENT"; identity: Identity }
  | { type: "RESTORED"; session: ConstructorParameters<typeof Agent>[0] }
  | { type: "RESTORE_EMPTY" }
  | { type: "IDENTITY_RESOLVED"; handle?: string | undefined; avatar?: string | undefined }
  | { type: "SIGN_OUT" }
  | { type: "SESSION_LOST"; did: string }
  | { type: "FORGET_ACCOUNT" };

// The single auth-shell state. Owned exclusively by authDispatch; read (only) by render(). Seeded to
// signedOut and immediately overwritten by authDispatch({type:"INIT"}) at main() start.
let auth: AuthState = { phase: "signedOut", identity: {} };

/** A display Identity from a persisted SessionHint (same fields; the hint's DID is required, ours optional). */
function identityFromHint(h: SessionHint): Identity {
  return { ...h };
}
// The expanded (post-unzip) files of the current build, kept so an edited site name can be honored by
// rebuilding before publish (the name is baked into the MASL → root CID, so it can't be swapped after).
let authoredFiles: FileInput[] = [];
// The signed-in account's published sites, listed after login and refreshed after each publish/delete.
let sites: SiteEntry[] = [];
// True while a loadSites() round-trip is in flight. The PDS answer takes a few seconds after login,
// and without this the "Your sites" section simply pops in late — with it, the section appears
// immediately holding a spinner row (see renderSites).
let sitesLoading = false;
// Monotonic id of the most recent loadSites() call. Login fires loadSites() and the legacy
// migration fires another on success; only the NEWEST call may settle the loading state or assign
// `sites`, so a slower earlier response can neither cancel a younger call's spinner nor render its
// staler list as if current. Bumped out-of-band on sign-out to strand any in-flight response.
let sitesRequestSeq = 0;
// True when the last settled loadSites() FAILED and its error is what #sites-notice shows. The
// notice slot is shared (see setSitesNotice), so a successful load clears the notice only while
// this is set — never a line some other flow (the legacy migration) wrote over our error.
let sitesLoadFailed = false;
// When set (via a site's "update" action), the next publish targets this existing site's rkey instead
// of minting a new site — publish() reuses the rkey and appends a new deployment (§8).
let updateTarget: SiteEntry | undefined;
// The version list last painted into the version-history panel (newest first), kept so the retention
// control can recompute its keep-last-N preview and build the prune plan from the same records the
// user is looking at. Reset whenever the panel closes or reloads.
let panelVersions: VersionEntry[] = [];
// The device-local external-mirror config (BYO CDN), if configured. When set, doPublish() builds a RASL
// producer from it so each publish also pushes blobs to the user's own bucket (§7.3). undefined = no mirror.
let r2Config: R2Config | undefined;
// The device-local external IPFS-mirror config (BYO IPFS), if configured. When set, doPublish() builds
// an IPFS producer (Kubo block/put → ipfs:<gateway> hint) alongside any R2 producer (§7.3). undefined = none.
let ipfsConfig: IpfsConfig | undefined;

/**
 * The primary serving URL for one of this account's sites. Prefers the derived per-site origin
 * `https://<siteRkey>--<did-plc-suffix>.<apex>/` (§11.3) — a pure function of (did, rkey), no lookup.
 * When that label can't be built (a `did:web` author, or an rkey that can't round-trip a DNS label —
 * `siteOriginLabel` returns null) it falls back to the site's claimed slug URL. Returns null when there
 * is no stable address at all, so callers omit the link rather than emit a broken href (the old apex
 * `/at/…` route is deleted — H1 Chunk 2 — and must never be reconstructed here).
 */
function siteUrl(site: SiteEntry): string | null {
  const did = agent?.did;
  const label = did !== undefined ? siteOriginLabel(did, site.rkey) : null;
  if (label !== null) return `https://${label}.${location.host}/`;
  if (site.slug !== undefined) return slugSiteUrl(site.slug, location.host);
  return null;
}

function dispatch(event: FlowEvent): void {
  state = reduce(state, event);
  render();
}

/**
 * The single owner of the auth-shell state (`auth`) and the `agent` credential. Every auth transition
 * routes through here: it updates `auth`, applies the coupled side effects (assigning/clearing `agent`
 * and syncing the localStorage hint + last-account via the write/clear helpers), then renders once. No
 * other code assigns `auth.*` or `agent` for auth purposes — that keeps illegal states unrepresentable.
 *
 * Invariant, made structurally true here: `auth.phase === "signedIn"` iff `agent?.did !== undefined`.
 * The ONLY transition that assigns `agent` (RESTORED) is the same one that moves phase→signedIn, and it
 * refuses signedIn without a DID; every signedOut/restoring transition leaves `agent` undefined. render()
 * and the publish gate therefore read the REAL `agent` for writes, never the optimistic phase.
 */
function authDispatch(event: AuthEvent): void {
  switch (event.type) {
    case "INIT": {
      // Read the optimistic hint + persistent last-account from localStorage. A hint means a session
      // likely exists → paint the signed-in shell optimistically (restoring) from the hinted identity,
      // so a returning user never sees the signed-out form flash during the async OAuth restore.
      const hint = readHint();
      const last = readLastAccount();
      const lastAccount = last !== undefined ? identityFromHint(last) : undefined;
      auth =
        hint !== undefined
          ? { phase: "restoring", identity: identityFromHint(hint), lastAccount }
          : { phase: "signedOut", identity: {}, lastAccount };
      break;
    }
    case "SIGN_IN_INTENT": {
      // Fired in signIn() BEFORE oauth.signIn() navigates, ONLY when a DID is known (card re-login of a
      // remembered account). Persisting the full hint here is what makes the OAuth return read it at INIT
      // and paint through the restoring path — the fix for the fresh-login signed-out→DID→handle flash.
      // No in-page phase change: navigation to the PDS happens immediately after.
      const { did, handle, avatar } = event.identity;
      if (did !== undefined) writeHint(did, handle, avatar);
      break;
    }
    case "RESTORED": {
      // oauth.init() returned a live session. Assigning `agent` and moving phase→signedIn together is the
      // sole path into signedIn, keeping the invariant true. A session with no DID can't gate writes, so
      // it degrades to signedOut rather than becoming a phantom signedIn.
      const restoredAgent = new Agent(event.session);
      const did = restoredAgent.did;
      if (did === undefined) {
        agent = undefined;
        clearHint();
        auth = { phase: "signedOut", identity: {}, lastAccount: auth.lastAccount };
        break;
      }
      agent = restoredAgent;
      // Merge the session DID into the (possibly hint-seeded) identity, KEEPING any hinted handle/avatar.
      const identity: Identity = { ...auth.identity, did };
      auth = { ...auth, phase: "signedIn", identity, notice: undefined };
      // Refresh the persisted hint's DID, preserving the known handle/avatar (loadIdentity() fills any gap).
      writeHint(did, identity.handle, identity.avatar);
      break;
    }
    case "RESTORE_EMPTY": {
      // oauth.init() returned undefined (no session, or a stale/expired hint). Drop the stale hint and
      // fall back to signed-out; KEEP lastAccount so the one-click re-login card still shows.
      agent = undefined;
      clearHint();
      auth = { phase: "signedOut", identity: {}, lastAccount: auth.lastAccount, notice: auth.notice };
      break;
    }
    case "IDENTITY_RESOLVED": {
      // loadIdentity() resolved the handle (and best-effort avatar). Accrete them into the identity
      // (never discarding a field already known), then persist the full hint + last-account so the next
      // load's optimistic UI shows the handle/avatar immediately instead of the raw DID.
      const identity: Identity = { ...auth.identity };
      if (event.handle !== undefined) identity.handle = event.handle;
      if (event.avatar !== undefined) identity.avatar = event.avatar;
      const did = identity.did;
      if (did !== undefined) {
        writeHint(did, identity.handle, identity.avatar);
        writeLastAccount(did, identity.handle, identity.avatar);
        auth = { ...auth, identity, lastAccount: { ...identity } };
      } else {
        auth = { ...auth, identity };
      }
      break;
    }
    case "SESSION_LOST":
    case "SIGN_OUT": {
      if (event.type === "SESSION_LOST" && event.did !== agent?.did &&
          !(auth.phase === "restoring" && auth.identity.did === event.did)) return;
      // Local credential availability owns the UI, not whether remote revocation
      // succeeds. Remember the current account even if identity hydration lost
      // its session before it could populate lastAccount.
      const lastAccount = auth.identity.did !== undefined ? { ...auth.identity } : auth.lastAccount;
      if (lastAccount?.did !== undefined) writeLastAccount(lastAccount.did, lastAccount.handle, lastAccount.avatar);
      agent = undefined;
      clearHint();
      auth = {
        phase: "signedOut", identity: {}, lastAccount,
        notice: event.type === "SESSION_LOST" ? SESSION_ENDED_NOTICE : undefined,
      };
      sites = [];
      sitesRequestSeq++; // fence all in-flight site/list/name results before their catches run
      sitesLoading = false;
      sitesLoadFailed = false;
      clearSitesNotice();
      updateTarget = undefined;
      renderSites();
      break;
    }
    case "FORGET_ACCOUNT": {
      // "Not me" / switch account: drop the persistent last-account record so the card no longer shows.
      clearLastAccount();
      auth = { ...auth, lastAccount: undefined };
      break;
    }
  }
  render();
}

function render(): void {
  // The security-critical read: `authed` reflects the REAL `agent`, never the optimistic `auth.phase`.
  // Publish/writes gate on this (canPublish below), so no write can fire against an unvalidated or
  // expired session even while the shell shows a signed-in phase during the optimistic restore.
  const authed = agent?.did !== undefined;
  // The auth shell is a pure function of `auth`. During `restoring` the signed-in shell is shown from
  // local data (the hint) so a returning user reads as "logged in" immediately — no spinner, since the
  // session restore is a passive background reconcile the user didn't initiate. Only Sign out is held
  // back until the REAL session lands (phase === "signedIn", which by invariant means `agent` is live).
  const showSignedIn = auth.phase !== "signedOut";
  ui.signedOut.classList.toggle("hidden", showSignedIn);
  ui.signedIn.classList.toggle("hidden", !showSignedIn);
  ui.whoText.classList.remove("hidden");
  ui.authLabel.textContent = "Signed in as";
  ui.signout.classList.toggle("hidden", auth.phase !== "signedIn");
  ui.authNotice.textContent = auth.notice ?? "";
  ui.authNotice.classList.toggle("hidden", auth.notice === undefined);
  // Prefer a resolved-or-hinted HANDLE over the raw DID. Because both restore paths seed `auth.identity`
  // from the hint BEFORE the agent lands (INIT) and RESTORED keeps the hinted handle, there's no DID
  // flash for a remembered account — the identity text is stable from first paint through resolution.
  ui.identity.textContent = auth.identity.handle ?? auth.identity.did ?? "";
  // The mirror configs (BYO CDN / IPFS) stay hidden for now, pending a UI rework — they keep their
  // HTML-default `hidden` class and nothing here reveals them. The mirror machinery (the ui.r2Section /
  // ui.ipfsSection refs and all the R2/IPFS save/clear/config code below) is intentionally left wired.

  // Show the remembered avatar during the optimistic restore too (from the hint), so the identity reads
  // as fully "logged in"; hidden only when there's no avatar to show at all.
  const shownAvatar = auth.identity.avatar;
  ui.avatar.classList.toggle("hidden", shownAvatar === undefined);
  if (shownAvatar !== undefined && ui.avatar.src !== shownAvatar) ui.avatar.src = shownAvatar;

  // Signed-out only: offer one-click re-login with the last-used account, shown as an avatar + @handle
  // row like the typeahead suggestions. Hidden while signed-in/restoring or when no account is remembered.
  const showLastAccount = auth.phase === "signedOut" && auth.lastAccount !== undefined;
  ui.lastAccount.classList.toggle("hidden", !showLastAccount);
  const lastAccount = auth.lastAccount;
  if (lastAccount !== undefined) {
    ui.continueHandle.textContent =
      lastAccount.handle !== undefined ? `@${lastAccount.handle}` : (lastAccount.did ?? "");
    const lastAvatar = lastAccount.avatar;
    ui.continueAvatar.classList.toggle("hidden", lastAvatar === undefined);
    if (lastAvatar !== undefined && ui.continueAvatar.src !== lastAvatar) ui.continueAvatar.src = lastAvatar;
  }

  ui.status.textContent = statusLabel(state);
  ui.status.classList.toggle("err", state.step === "error");
  ui.publish.disabled = !canPublish(state, authed);
  ui.publish.textContent = updateTarget !== undefined ? "Publish update" : "Publish";

  // The editable name field appears once a build exists (its value can then be edited before publish).
  const hasBuild =
    state.step === "ready" ||
    state.step === "previewed" ||
    state.step === "publishing" ||
    (state.step === "error" && state.built !== undefined);
  ui.nameField.classList.toggle("hidden", !hasBuild);
  // The sandboxed preview iframe (§14): shown from the moment a build exists (it renders the built
  // site there), and it MUST render before Publish enables — canPublish gates publish on `previewed`,
  // which is reached only by the iframe's load event (see main()'s "load" listener).
  ui.preview.classList.toggle("hidden", !hasBuild);

  ui.updateBanner.classList.toggle("hidden", updateTarget === undefined);
  ui.updateName.textContent = updateTarget?.title ?? "";
  // The version-history panel only makes sense while editing a specific site; loadVersions() fills it.
  ui.versionHistory.classList.toggle("hidden", updateTarget === undefined);
}

/**
 * A `currentColor` SVG glyph for an `.icon` button, given its inner `<path>`/`<circle>` markup.
 * Used where no single Unicode glyph renders reliably across system fonts (the download tray, the
 * short-name tag) — and, being currentColor, these also pick up the button's hover color, which the
 * emoji glyphs (🗑) cannot. `paths` is a fixed string literal at every call site (no user input).
 */
function iconSvg(paths: string): string {
  return (
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"` +
    ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`
  );
}

/** Whether `#sites-section` should be visible: sites loading (spinner row), real sites to show, or
 *  the migration notice (see setSitesNotice) has something to report. A single predicate so
 *  renderSites() and setSitesNotice()/clearSitesNotice() compose instead of racing to toggle the same
 *  "hidden" class — a user with ONLY legacy sites has zero listed sites at the moment migration
 *  starts, so the notice must be able to hold the section open on its own. */
function sitesSectionVisible(): boolean {
  return (
    (agent?.did !== undefined && (sitesLoading || sites.length > 0)) ||
    !ui.sitesNotice.classList.contains("hidden")
  );
}

/** Rebuild the "your sites" list from `sites`; hidden entirely when signed-out, nothing published, and
 *  the migration notice has nothing to show (see sitesSectionVisible). While a load is in flight and
 *  nothing is listed yet, the list holds a single spinner row instead — so the section appears the
 *  moment the shell signs in rather than seconds later when the PDS finally answers. A re-load with
 *  entries already on screen keeps them (a brief staleness beats a flash of empty + spinner). */
function renderSites(): void {
  ui.sitesSection.classList.toggle("hidden", !sitesSectionVisible());
  ui.sites.replaceChildren();
  if (sitesLoading && sites.length === 0) {
    const loading = document.createElement("li");
    loading.className = "sites-loading";
    const label = document.createElement("span");
    label.textContent = "Loading your sites…";
    loading.append(label);
    ui.sites.append(loading);
    return;
  }
  for (const site of sites) {
    const li = document.createElement("li");
    li.className = "site";
    li.dataset.rkey = site.rkey;

    // Clicking the name reveals an inline rename editor (rewrites the pointer title; always available).
    const nameEditor = document.createElement("div");
    nameEditor.className = "slug-editor hidden";
    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "slug-input";
    nameInput.value = site.title;
    const nameGo = document.createElement("button");
    nameGo.className = "icon";
    nameGo.type = "button";
    nameGo.textContent = "Save";
    const submitName = (): void => void renameSite(site, nameInput.value);
    nameGo.addEventListener("click", submitName);
    nameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submitName();
      } else if (e.key === "Escape") {
        nameEditor.classList.add("hidden");
      }
    });
    nameEditor.append(nameInput, nameGo);

    const name = document.createElement("span");
    name.className = "site-name";
    name.textContent = site.title;
    name.title = "Rename";
    name.setAttribute("role", "button");
    name.tabIndex = 0;
    name.addEventListener("click", () => {
      const nowHidden = nameEditor.classList.toggle("hidden");
      if (!nowHidden) {
        nameInput.value = site.title;
        nameInput.focus();
        nameInput.select();
      }
    });

    const size = document.createElement("span");
    size.className = "site-size";
    size.textContent = sizeLabel(site.sizeBytes);

    const actions = document.createElement("span");
    actions.className = "site-actions";

    // The site's live address: the derived per-site origin, else a claimed slug, else none (a did:web
    // author with no slug yet has no stable URL — the Visit/Download links are omitted rather than broken).
    const url = siteUrl(site); // stable identity: exports must not follow a movable route
    const visitUrl = preferredVisitUrl(agent?.did, site.names ?? [], location.host, url);
    let visit: HTMLAnchorElement | undefined;
    if (visitUrl !== null) {
      visit = document.createElement("a");
      visit.className = "icon";
      visit.href = visitUrl;
      visit.target = "_blank";
      visit.rel = "noopener";
      visit.title = "Visit";
      visit.setAttribute("aria-label", "Visit");
      visit.textContent = "↗";
    }

    const update = document.createElement("button");
    update.className = "icon";
    update.type = "button";
    update.title = "Update (publish new files to this site)";
    update.setAttribute("aria-label", "Update");
    update.textContent = "✎";
    update.addEventListener("click", () => beginUpdate(site));

    // An inline short-name editor, revealed under the row by the tag action (replaces the old prompt()).
    const slugEditor = document.createElement("div");
    slugEditor.className = "slug-editor hidden";
    const slugInput = document.createElement("input");
    slugInput.type = "text";
    slugInput.className = "slug-input";
    slugInput.placeholder = "short-name";
    slugInput.value = ROUTE_COLLECTION ? site.names?.[0] ?? "" : site.slug ?? "";
    slugInput.autocapitalize = "none";
    slugInput.autocomplete = "off";
    slugInput.spellcheck = false;
    const slugGo = document.createElement("button");
    slugGo.className = "icon";
    slugGo.type = "button";
    slugGo.textContent = ROUTE_COLLECTION ? "Name" : "Claim";
    const submitSlug = (): void => {
      if (ROUTE_COLLECTION) void nameSite(site, slugInput.value);
      else void claimSlug(site, slugInput.value);
    };
    slugGo.addEventListener("click", submitSlug);
    slugInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        submitSlug();
      } else if (e.key === "Escape") {
        slugEditor.classList.add("hidden");
      }
    });
    slugEditor.append(slugInput, slugGo);

    const claim = document.createElement("button");
    claim.className = "icon";
    claim.type = "button";
    claim.title = ROUTE_COLLECTION ? "Name this site in your publisher namespace" : "Claim a short name (a <name>.szzt.app URL)";
    claim.setAttribute("aria-label", ROUTE_COLLECTION ? "Name site" : "Claim short name");
    // A tag glyph — this claims a *name* for the site, not a generic link.
    claim.innerHTML = iconSvg(
      '<path d="M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7Z"/>' +
        '<circle cx="7.5" cy="7.5" r="1.3"/>',
    );
    claim.addEventListener("click", () => {
      const nowHidden = slugEditor.classList.toggle("hidden");
      if (!nowHidden) {
        slugInput.focus();
        slugInput.select();
      }
    });

    // Whole-site export (roadmap item 17): a plain link, no handler — the gateway answers with
    // `content-disposition: attachment`, so a click prompts the browser's save dialog. The `download`
    // attribute is belt-and-braces for this same-origin case; it is IGNORED cross-origin, which is
    // exactly why the header (not this attribute) is what actually makes the download work.
    let download: HTMLAnchorElement | undefined;
    if (url !== null) {
      download = document.createElement("a");
      download.className = "icon";
      download.href = exportUrl(url);
      download.setAttribute("download", "");
      download.title = "Download this whole site as a zip";
      download.setAttribute("aria-label", "Download as zip");
      // Down-arrow into a tray. An SVG, not a glyph: the Unicode download arrow (⭳) is absent from
      // many system fonts and rendered as tofu.
      download.innerHTML = iconSvg('<path d="M12 3v11"/><path d="m7 10 5 4 5-4"/><path d="M4 20h16"/>');
    }

    const del = document.createElement("button");
    del.className = "icon danger";
    del.type = "button";
    del.title = "Delete";
    del.setAttribute("aria-label", "Delete");
    del.textContent = "🗑";
    del.addEventListener("click", () => void deleteSite(site));

    const actionEls = [visit, claim, update, download, del].filter(
      (el): el is HTMLAnchorElement | HTMLButtonElement => el !== undefined,
    );
    actions.append(...actionEls);
    const label = document.createElement("span");
    label.className = "site-label";
    label.append(name, size);
    // A short-name link, shown once a slug is claimed for this site this session (§11.1).
    if (site.slug !== undefined) {
      const slugLink = document.createElement("a");
      slugLink.className = "site-slug";
      slugLink.href = slugSiteUrl(site.slug, location.host);
      slugLink.target = "_blank";
      slugLink.rel = "noopener";
      slugLink.textContent = `${site.slug}.${location.host}`;
      label.append(slugLink);
    }
    for (const shortName of site.names ?? []) {
      const namedUrl = namedSiteUrl(agent?.did ?? "", shortName, location.host);
      if (namedUrl === null) continue;
      const link = document.createElement("a");
      link.className = "site-slug";
      link.href = namedUrl;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = new URL(namedUrl).host;
      label.append(link);
    }
    // Label + actions share one non-wrapping header row; the editors stack full-width beneath it.
    const siteRow = document.createElement("div");
    siteRow.className = "site-row";
    siteRow.append(label, actions);
    li.append(siteRow, slugEditor, nameEditor);
    ui.sites.append(li);
  }
}

/** A site's size shown in parens (e.g. " (12 KB)"), or empty while it is still being computed. */
function sizeLabel(bytes: number | undefined): string {
  if (bytes === undefined) return "";
  const s = formatSize(bytes);
  return s === "" ? "" : ` (${s})`;
}

// A read-only RepoReader over the signed-in agent, for resolveTarget() when computing site sizes.
// getBlob is never reached (resolveTarget only fetches records), so it throws if ever called.
function agentRepoReader(a: Agent): RepoReader {
  return {
    async getRecord(atUri: string): Promise<FetchedRecord | null> {
      const m = /^at:\/\/([^/]+)\/([^/]+)\/([^/]+)$/.exec(atUri);
      if (m === null) return null;
      const [, repo, collection, rkey] = m as unknown as [string, string, string, string];
      try {
        const res = await a.com.atproto.repo.getRecord({ repo, collection, rkey });
        return { uri: res.data.uri, cid: res.data.cid ?? "", value: res.data.value };
      } catch {
        return null; // best-effort sizing: any fetch failure reads as "no size"
      }
    },
    getBlob(): Promise<Uint8Array> {
      throw new Error("getBlob unsupported in the publisher size reader");
    },
  };
}

// Lazily compute each listed site's total content size (site → deployment → MASL, summing the inline
// per-blob sizes — no blob fetches) and patch it into the list as each resolves. Best-effort: a site
// that fails to resolve simply shows no size.
async function hydrateSizes(): Promise<void> {
  const actingAgent = agent;
  if (actingAgent?.did === undefined) return;
  const did = actingAgent.did;
  const reader = agentRepoReader(actingAgent);
  await Promise.all(
    sites.map(async (site) => {
      if (site.sizeBytes !== undefined) return;
      try {
        const resolved = await resolveTarget(reader, `at://${did}/${COLLECTION.site}/${site.rkey}`);
        const total = totalResourceBytes(resolved.manifest.value.tile.resources);
        if (total === undefined || agent !== actingAgent) return;
        site.sizeBytes = total;
        const cell = ui.sites.querySelector<HTMLSpanElement>(
          `li[data-rkey="${CSS.escape(site.rkey)}"] .site-size`,
        );
        if (cell !== null) cell.textContent = sizeLabel(total);
      } catch {
        // leave the size blank
      }
    }),
  );
}

// Unpack any dropped/selected `.zip` into its constituent files (a whole site zipped up), leaving
// non-archive files as-is, so a zip, a folder, loose files, or a bare image all flow into build() the
// same way. The verified decompression-bomb guards live in unzipToFileInputs (@szzt/frontend).
async function expandArchives(files: FileInput[]): Promise<FileInput[]> {
  const out: FileInput[] = [];
  for (const f of files) {
    if (f.path.toLowerCase().endsWith(".zip")) {
      out.push(...(await unzipToFileInputs(f.bytes)));
    } else {
      out.push(f);
    }
  }
  return out;
}

/** Rebuild the scrollable panel listing the currently staged files (paths + sizes); hidden when empty.
 * Shown for both a new drop and an update — `authoredFiles` is populated the same way for each. */
function renderFileList(): void {
  const files = authoredFiles;
  ui.fileList.classList.toggle("hidden", files.length === 0);
  ui.fileListCount.textContent = files.length === 1 ? "1 file" : `${files.length} files`;
  ui.fileListItems.replaceChildren();
  for (const f of files) {
    const li = document.createElement("li");
    const path = document.createElement("span");
    path.className = "file-path";
    path.textContent = f.path;
    path.title = f.path;
    const bytes = document.createElement("span");
    bytes.className = "file-bytes";
    bytes.textContent = formatSize(f.bytes.length);
    li.append(path, bytes);
    ui.fileListItems.append(li);
  }
}

/** Discard the currently staged drop (files + name) and return the drop area to empty. Keeps any active
 * update target so its banner stays (re-drop to supply new files); the banner's own ✕ cancels the update. */
function clearStagedBuild(): void {
  authoredFiles = [];
  ui.siteName.value = "";
  ui.previewFrame.srcdoc = ""; // drop the stale sandboxed preview; render() hides the panel on RESET
  renderFileList();
  dispatch({ type: "RESET" });
}

async function onFiles(rawFiles: FileInput[]): Promise<void> {
  if (rawFiles.length === 0) return;
  dispatch({ type: "BUILD_START" });
  try {
    const files = await expandArchives(rawFiles);
    authoredFiles = files;
    renderFileList();
    const name = deriveInitialName(files);
    ui.siteName.value = name; // prefill the editable name (index <title>, else file/folder stem)
    const built = await build(files, { name });
    dispatch({ type: "BUILD_OK", built });
    // §14 preview step: render the just-built site in the sandboxed iframe. When it renders, the
    // iframe's `load` event dispatches PREVIEW_OK, which is what unlocks Publish (canPublish gates on
    // `previewed`). A build that can't be previewed can't be published — the catch leaves the flow in
    // `ready`, so Publish stays disabled.
    await showPreview(built);
  } catch (err) {
    dispatch({ type: "BUILD_ERR", message: errMessage(err) });
  }
}

/** Render `built` into the sandboxed preview iframe (§14). Setting `srcdoc` fires the iframe's `load`
 * event, which dispatches PREVIEW_OK (see main()) to promote the flow `ready` -> `previewed` and
 * thereby enable Publish. Kept separate so an update-drop previews identically to a new drop. */
async function showPreview(built: BuildResult): Promise<void> {
  try {
    await mountPreview(ui.previewFrame, createPreview(built));
  } catch (err) {
    // A preview that fails to serve is a build that would fail to serve live — surface it and leave
    // the flow in `ready` (Publish stays gated), rather than letting an unpreviewable site publish.
    setStatus(`Preview failed: ${errMessage(err)}`, true);
  }
}

/** Set the mirror-section status line + its ok/err styling. */
function setR2Status(text: string, kind: "" | "ok" | "err"): void {
  ui.r2Status.textContent = text;
  ui.r2Status.classList.toggle("ok", kind === "ok");
  ui.r2Status.classList.toggle("err", kind === "err");
}

/** Reflect the saved mirror config into the R2 form (prefill fields) + status line. Called only at
 *  startup and after an explicit save/clear — never on every render, so it can't clobber in-progress
 *  edits. Section visibility (signed-in only) is owned by render(). */
function renderR2(): void {
  if (r2Config !== undefined) {
    ui.r2Account.value = r2Config.accountId;
    ui.r2Bucket.value = r2Config.bucket;
    ui.r2Key.value = r2Config.accessKeyId;
    ui.r2Secret.value = r2Config.secretAccessKey;
    ui.r2Host.value = r2Config.host;
    setR2Status(`Mirror active — published blobs also push to ${r2Config.host}.`, "ok");
  } else {
    setR2Status("No mirror configured — sites publish to your PDS only.", "");
  }
}

/** Persist the mirror config from the form, requiring all five fields. The host is normalized to the
 *  bare-host form the gateway expects before saving. */
function saveR2(): void {
  const cfg: R2Config = {
    accountId: ui.r2Account.value.trim(),
    bucket: ui.r2Bucket.value.trim(),
    accessKeyId: ui.r2Key.value.trim(),
    secretAccessKey: ui.r2Secret.value.trim(),
    host: normalizeMirrorHost(ui.r2Host.value),
  };
  if (Object.values(cfg).some((v) => v === "")) {
    setR2Status("Fill in every field to enable the mirror.", "err");
    return;
  }
  r2Config = cfg;
  writeR2Config(cfg);
  renderR2(); // reflect the normalized host + active status
}

/** Forget the mirror config (device-local) and empty the form. */
function clearR2(): void {
  r2Config = undefined;
  clearR2Config();
  ui.r2Account.value = "";
  ui.r2Bucket.value = "";
  ui.r2Key.value = "";
  ui.r2Secret.value = "";
  ui.r2Host.value = "";
  renderR2();
}

/** Set the IPFS-mirror-section status line + its ok/err styling. */
function setIpfsStatus(text: string, kind: "" | "ok" | "err"): void {
  ui.ipfsStatus.textContent = text;
  ui.ipfsStatus.classList.toggle("ok", kind === "ok");
  ui.ipfsStatus.classList.toggle("err", kind === "err");
}

/** Reflect the saved IPFS-mirror config into its form (prefill fields) + status line. Called only at
 *  startup and after an explicit save/clear — never on every render, so it can't clobber in-progress
 *  edits. Section visibility (signed-in only) is owned by render(). */
function renderIpfs(): void {
  if (ipfsConfig !== undefined) {
    ui.ipfsApi.value = ipfsConfig.apiUrl;
    ui.ipfsGateway.value = ipfsConfig.gatewayHost;
    ui.ipfsAuth.value = ipfsConfig.authorization ?? "";
    setIpfsStatus(`Mirror active — published blocks also push to ${ipfsConfig.gatewayHost}.`, "ok");
  } else {
    setIpfsStatus("No IPFS mirror configured — sites publish to your PDS only.", "");
  }
}

/** Persist the IPFS-mirror config from the form, requiring the write-API URL + gateway host (the auth
 *  header is optional). The gateway host is normalized to the bare-host form the gateway expects; the
 *  write-API URL keeps its scheme/port (it's a full endpoint, not a hint). */
function saveIpfs(): void {
  const apiUrl = ui.ipfsApi.value.trim();
  const gatewayHost = normalizeMirrorHost(ui.ipfsGateway.value);
  const authorization = ui.ipfsAuth.value.trim();
  if (apiUrl === "" || gatewayHost === "") {
    setIpfsStatus("Enter both the IPFS write API URL and the public gateway host.", "err");
    return;
  }
  const cfg: IpfsConfig = { apiUrl, gatewayHost };
  if (authorization !== "") cfg.authorization = authorization;
  ipfsConfig = cfg;
  writeIpfsConfig(cfg);
  renderIpfs(); // reflect the normalized host + active status
}

/** Forget the IPFS-mirror config (device-local) and empty the form. */
function clearIpfs(): void {
  ipfsConfig = undefined;
  clearIpfsConfig();
  ui.ipfsApi.value = "";
  ui.ipfsGateway.value = "";
  ui.ipfsAuth.value = "";
  renderIpfs();
}

async function doPublish(): Promise<void> {
  const authed = agent?.did !== undefined;
  // canPublish already requires `previewed`; the `state.step` check narrows the type and makes the
  // §14 gate explicit here too — publish is unreachable until the sandboxed preview has rendered.
  if (agent === undefined || !canPublish(state, authed) || state.step !== "previewed") return;
  let built = state.built;
  const target = updateTarget;
  const name = ui.siteName.value.trim();
  dispatch({ type: "PUBLISH_START", authed });
  try {
    // The name is baked into the build (root CID), so an edit since the drop requires a rebuild. Build
    // is pure + deterministic; rebuilding with the same name yields the identical result.
    if (name !== "" && name !== built.manifest.name && authoredFiles.length > 0) {
      built = await build(authoredFiles, { name });
    }
    // On an update, thread the version being replaced as `prev` so the deployment records form a
    // manifest lineage chain (§4.3). Best-effort: read the current deployment's `tile.cid`; if it can't
    // be resolved, publish anyway with no `prev` (lineage is informational — version history relies on
    // the `site` back-reference, not this chain, so a missing link must never block a publish).
    let prevManifestCid: string | undefined;
    if (target?.currentDeploymentUri !== undefined) {
      prevManifestCid = await currentManifestCid(target.currentDeploymentUri);
    }
    // If any mirror is configured, also push every blob + the manifest to the user's own bucket/IPFS
    // node and advertise it in the site record's `hints` (§7.3). Each mirror is INDEPENDENT and
    // best-effort inside publish() — a push failure drops only that mirror's hint, but the
    // authoritative PDS publish always proceeds.
    const producers: MirrorProducer[] = [];
    if (r2Config !== undefined) {
      producers.push(raslBucketProducer({ host: r2Config.host, put: r2BucketPut(r2Config) }));
    }
    if (ipfsConfig !== undefined) {
      const kuboConfig =
        ipfsConfig.authorization !== undefined
          ? { apiUrl: ipfsConfig.apiUrl, authorization: ipfsConfig.authorization }
          : { apiUrl: ipfsConfig.apiUrl };
      producers.push(
        ipfsBlockProducer({ gatewayHost: ipfsConfig.gatewayHost, put: kuboBlockPut(kuboConfig) }),
      );
    }
    await publish({
      client: atpClient(agent),
      built,
      // Set → update the existing site in place (reuse its rkey); unset → mint a new site (§8).
      ...(target !== undefined ? { siteRkey: target.rkey } : {}),
      ...(prevManifestCid !== undefined ? { prevManifestCid } : {}),
      ...(producers.length > 0 ? { producers } : {}),
      meta: { title: built.manifest.name },
    });
    // Success: reset the "new" drop area to its initial empty state — clear the staged files + name
    // field, and RESET the flow to `idle` (re-disables Publish, restores the "Drop a file…" prompt).
    // The just-published site now surfaces in "Your sites" below (loadSites lists it, newest first).
    updateTarget = undefined;
    authoredFiles = [];
    ui.siteName.value = "";
    ui.previewFrame.srcdoc = ""; // clear the sandboxed preview; render() hides the panel on RESET
    clearVersionPanel();
    renderFileList();
    dispatch({ type: "RESET" });
    void loadSites();
  } catch (err) {
    dispatch({ type: "PUBLISH_ERR", message: errMessage(err) });
  }
}

/** Arm the next publish to replace an existing site's content instead of creating a new one. */
function beginUpdate(site: SiteEntry): void {
  updateTarget = site;
  render();
  void loadVersions(site);
  ui.drop.scrollIntoView({ behavior: "smooth", block: "center" });
}

function cancelUpdate(): void {
  updateTarget = undefined;
  clearVersionPanel();
  render();
}

/** Empty the version-history panel and its retention control, and drop the retained version list. */
function clearVersionPanel(): void {
  panelVersions = [];
  ui.versionList.replaceChildren();
  ui.retention.classList.add("hidden");
  ui.retentionHint.classList.add("hidden");
}

/** Delete a site's pointer record so it stops resolving. Deployment/manifest version records for it
 * are left as unreferenced history (harmless; the reader only follows the live `app.szzt.site`). */
async function deleteSite(site: SiteEntry): Promise<void> {
  if (agent?.did === undefined) return;
  if (!confirm(`Delete "${site.title}"? Its URL will stop resolving.`)) return;
  try {
    await agent.com.atproto.repo.applyWrites({
      repo: agent.did,
      writes: [
        {
          $type: "com.atproto.repo.applyWrites#delete",
          collection: COLLECTION.site,
          rkey: site.rkey,
        },
      ],
    });
    if (updateTarget?.rkey === site.rkey) updateTarget = undefined;
    await loadSites();
    render();
  } catch (err) {
    setStatus(`Delete failed: ${errMessage(err)}`, true);
  }
}

// Publisher-controlled names can move/reuse origins. A record CID is explicit
// authorization, not a retry hint: never silently adopt a competing writer's edit.
async function nameSite(site: SiteEntry, raw: string): Promise<void> {
  const actingAgent = agent;
  if (actingAgent?.did === undefined || !ROUTE_COLLECTION) return;
  const did = actingAgent.did;
  const name = raw.trim(); // no case folding of ATW record keys
  const url = namedSiteUrl(did, name, location.host);
  if (url === null) {
    setStatus("Use a lowercase name with single internal hyphens (up to 36 characters); publisher-scoped HTTPS names require a did:plc identity.", true);
    return;
  }
  try {
    const client = atpClient(actingAgent);
    const prior = await client.getRecord(ROUTE_COLLECTION, name);
    if (agent !== actingAgent) return;
    if (prior !== null) {
      const record = validateRouteRecord(prior.value, ROUTE_COLLECTION);
      if (record.policy !== undefined && Object.keys(record.policy).length > 0) {
        throw new Error("This name has an ATW policy that HTTPS cannot enforce. Inspect and explicitly edit it with the route CLI first.");
      }
    }
    if (prior !== null && !confirm(`Move or reuse ${name} for this site? This address keeps its existing browser storage and permissions. You are responsible for that continuity.`)) return;
    setStatus(`Naming ${name}…`, false);
    const result = await writeRoute({
      client, collection: ROUTE_COLLECTION, name,
      intent: { kind: "site", siteRkey: site.rkey }, expectedCid: prior?.cid ?? null,
    });
    if (agent !== actingAgent) return;
    setStatus(`${result.unchanged ? "Already named" : "Named"}: ${url}${result.warning ? ` — ${result.warning}` : ""}`, false);
    await loadSites();
  } catch (err) {
    if (agent === actingAgent) setStatus(`Could not name site — ${errMessage(err)}`, true);
  }
}

/** Claim a gateway slug (§11.1) for a site: a `<slug>.<apex>` short-name URL pointing at this site.
 * Takes the name from the row's inline slug input (revealed by the tag action), mints a service-auth JWT
 * scoped to the gateway service DID (did:web:<apex>), and POSTs the same-origin allocate-slug endpoint.
 * On success the row shows the short-name link; failures (taken / confusable / reserved / …) surface in
 * the status line. */
async function claimSlug(site: SiteEntry, raw: string): Promise<void> {
  if (agent?.did === undefined) return;
  const did = agent.did;
  const slug = normalizeSlug(raw);
  const localErr = slugInputError(slug);
  if (localErr !== undefined) {
    setStatus(localErr, true);
    return;
  }
  setStatus(`Claiming ${slug}.${location.host}…`, false);
  try {
    // Mint a short-lived atproto service-auth JWT: signed by the account's DID key, audience = the
    // gateway service DID. The gateway verifies the signature + audience, then the own-DID gate ties
    // the claim to this account (a publisher may only claim slugs for its own sites, §11.1).
    const { data } = await agent.com.atproto.server.getServiceAuth({
      aud: serviceDidFor(location.hostname),
      lxm: SLUG_ALLOC_LXM,
    });
    const res = await fetch(new URL(SLUG_ALLOC_PATH, location.origin), {
      method: "POST",
      headers: { authorization: `Bearer ${data.token}`, "content-type": "application/json" },
      body: JSON.stringify({ slug, siteRkey: site.rkey, did }),
    });
    const outcome: unknown = await res.json().catch(() => undefined);
    if (res.ok) {
      site.slug = slug;
      renderSites();
      setStatus(`Claimed ${slug}.${location.host}`, false);
    } else {
      setStatus(slugOutcomeError(outcome), true);
    }
  } catch (err) {
    setStatus(`Claim failed: ${errMessage(err)}`, true);
  }
}

/** Rename a published site by rewriting its `app.szzt.site` pointer record's `title` — a cheap
 * single-record update (no rebuild, no new deployment), so it's always available on an existing site.
 * The title is pointer METADATA, independent of the content hash; it does NOT change a synthesized
 * index page's baked-in <title> (that lives in the content-addressed MASL), and authored pages carry
 * their own <title> regardless. The site record holds no cid-links (its `current.cid` is a plain
 * `format: cid` string), so reading the value back and writing it verbatim round-trips safely. */
async function renameSite(site: SiteEntry, rawName: string): Promise<void> {
  if (agent?.did === undefined) return;
  const name = rawName.trim();
  if (name === "") {
    setStatus("Enter a site name.", true);
    return;
  }
  if (name === site.title) {
    renderSites(); // no change — just close the editor
    return;
  }
  try {
    const res = await agent.com.atproto.repo.getRecord({
      repo: agent.did,
      collection: COLLECTION.site,
      rkey: site.rkey,
    });
    const value = (res.data.value ?? {}) as Record<string, unknown>;
    await agent.com.atproto.repo.applyWrites({
      repo: agent.did,
      writes: [
        {
          $type: "com.atproto.repo.applyWrites#update",
          collection: COLLECTION.site,
          rkey: site.rkey,
          value: { ...value, title: name },
        },
      ],
    });
    site.title = name;
    if (updateTarget?.rkey === site.rkey) updateTarget.title = name;
    renderSites();
    render(); // the update banner may be showing this site's title
    setStatus(`Renamed to "${name}".`, false);
  } catch (err) {
    setStatus(`Rename failed: ${errMessage(err)}`, true);
  }
}

/** One prior deployment of the site being edited, as shown in the version-history panel. */
interface VersionEntry {
  rkey: string;
  /** The deployment's AT-URI + atproto CID — the strong ref written to `site.current` on revert. */
  uri: string;
  cid: string;
  /** AT-URI of the paired `ing.dasl.masl` manifest record (deployment `tile.uri`) — the record a prune
   *  deletes alongside the deployment (§16). Empty when the deployment record carries no `tile.uri`. */
  manifestUri: string;
  createdAt: string | undefined;
  isCurrent: boolean;
}

/** A deployment record value's ISO `createdAt`, or undefined if absent/malformed. */
function deploymentCreatedAt(value: unknown): string | undefined {
  const c = (value as { createdAt?: unknown } | null)?.createdAt;
  return typeof c === "string" ? c : undefined;
}

/** A deployment record value's `tile.uri` (its manifest record AT-URI), or "" if absent/malformed. */
function deploymentManifestUri(value: unknown): string {
  const uri = (value as { tile?: { uri?: unknown } } | null)?.tile?.uri;
  return typeof uri === "string" ? uri : "";
}

/** Human-readable timestamp for a deployment, from its ISO `createdAt`. */
function formatWhen(iso: string | undefined): string {
  if (iso === undefined) return "unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** List the deployments belonging to `site` (via the deployment `site` back-reference, §4.2) and render
 *  the version-history panel. Deployments predating the `site` field are unattributable and excluded —
 *  but the site's live deployment is always shown (fetched by `current.uri`) so at minimum the current
 *  version appears; older versions each get a "Revert" that re-points the site at that deployment. */
async function loadVersions(site: SiteEntry): Promise<void> {
  if (agent?.did === undefined) return;
  clearVersionPanel();
  const currentRkey = site.currentDeploymentUri?.split("/").pop();
  try {
    const res = await agent.com.atproto.repo.listRecords({
      repo: agent.did,
      collection: COLLECTION.deployment,
      limit: 100,
    });
    const versions: VersionEntry[] = res.data.records
      .filter((r) => (r.value as { site?: unknown } | null)?.site === site.rkey)
      .map((r) => {
        const rkey = r.uri.split("/").pop() ?? "";
        return {
          rkey,
          uri: r.uri,
          cid: r.cid,
          manifestUri: deploymentManifestUri(r.value),
          createdAt: deploymentCreatedAt(r.value),
          isCurrent: rkey === currentRkey,
        };
      });
    // Older sites' live deployment predates the `site` field, so it won't be in the list above —
    // fetch it by its rkey so the current version is always represented.
    if (currentRkey !== undefined && !versions.some((v) => v.isCurrent)) {
      const cur = await agent.com.atproto.repo
        .getRecord({ repo: agent.did, collection: COLLECTION.deployment, rkey: currentRkey })
        .catch(() => undefined);
      if (cur !== undefined) {
        versions.push({
          rkey: currentRkey,
          uri: cur.data.uri,
          cid: cur.data.cid ?? "",
          manifestUri: deploymentManifestUri(cur.data.value),
          createdAt: deploymentCreatedAt(cur.data.value),
          isCurrent: true,
        });
      }
    }
    versions.sort((a, b) => (a.rkey < b.rkey ? 1 : -1)); // newest first (TID rkeys sort chronologically)
    // A fast cancel/switch could have moved on while we awaited — only paint if still editing this site.
    if (updateTarget?.rkey === site.rkey) renderVersions(site, versions);
  } catch {
    // Leave the panel empty on failure; the update flow itself is unaffected.
  }
}

/** Paint the version-history list for `site` from its resolved deployments (newest first). */
function renderVersions(site: SiteEntry, versions: VersionEntry[]): void {
  panelVersions = versions;
  ui.versionList.replaceChildren();
  if (versions.length === 0) {
    const empty = document.createElement("li");
    empty.className = "vh-empty";
    empty.textContent = "No earlier versions yet — publishing an update adds one.";
    ui.versionList.append(empty);
    renderRetention(site);
    return;
  }
  for (const v of versions) {
    const li = document.createElement("li");
    const when = document.createElement("span");
    when.className = "vh-when";
    when.textContent = formatWhen(v.createdAt);
    li.append(when);
    if (v.isCurrent) {
      const tag = document.createElement("span");
      tag.className = "vh-current";
      tag.textContent = "current";
      li.append(tag);
    } else {
      const revert = document.createElement("button");
      revert.className = "secondary";
      revert.type = "button";
      revert.textContent = "Revert";
      revert.title = "Point this site back at this version";
      revert.addEventListener("click", () => void revertTo(site, v));
      li.append(revert);
    }
    ui.versionList.append(li);
  }
  renderRetention(site);
}

/** The site's active deployment URI as the retention primitives need it — never a prune target (§16). */
function currentUriOf(site: SiteEntry): string {
  return site.currentDeploymentUri ?? panelVersions.find((v) => v.isCurrent)?.uri ?? "";
}

/** Map the panel's version list into the pure retention module's shape (deployment + manifest URIs +
 *  date). `blobCids` are left unresolved here — the live preview never needs them, and applyRetention
 *  resolves them just before executing so the releasable-blob report is faithful. */
function retentionVersions(): RetentionVersion[] {
  return panelVersions.map((v) => ({
    deploymentUri: v.uri,
    manifestUri: v.manifestUri,
    createdAt: v.createdAt,
  }));
}

/**
 * Refresh the keep-last-N retention control from the panel's current versions (§16, §17.6). This is the
 * explicit, opt-in "delete old versions" control (roadmap item 9 — never automatic): it previews, via
 * `previewKeepLast` (→ `planRetention`), how many older versions the chosen keep count would delete, and
 * enables Apply only when that count is non-zero. Pruning below two versions is impossible (only the
 * current one exists, which is never pruned), so the control is hidden there.
 */
function renderRetention(site: SiteEntry): void {
  const prunable = panelVersions.length > 1;
  ui.retention.classList.toggle("hidden", !prunable);
  ui.retentionHint.classList.toggle("hidden", !prunable);
  if (!prunable) return;
  const keep = clampKeep(ui.retentionKeep.valueAsNumber);
  if (String(keep) !== ui.retentionKeep.value) ui.retentionKeep.value = String(keep);
  const plan = previewKeepLast(retentionVersions(), keep, currentUriOf(site));
  ui.retentionHint.textContent = retentionSummary(plan);
  ui.retentionApply.disabled = plan.prune.length === 0;
}

/** Resolve a deployment's manifest resource blob CIDs (for the releasable-blob report), or [] if the
 *  manifest can't be read — [] only under-reports releasable blobs, never affecting the deletes (§16). */
async function versionBlobCids(manifestUri: string): Promise<string[]> {
  if (agent?.did === undefined || manifestUri === "") return [];
  const rkey = manifestUri.split("/").pop();
  if (rkey === undefined || rkey === "") return [];
  try {
    const res = await agent.com.atproto.repo.getRecord({
      repo: agent.did,
      collection: COLLECTION.masl,
      rkey,
    });
    const tile = (res.data.value as { tile?: unknown } | null)?.tile;
    if (tile === null || typeof tile !== "object") return [];
    return manifestBlobCids(tile as MaslDocument);
  } catch {
    return [];
  }
}

/**
 * Apply the keep-last-N retention policy: delete the older versions the plan selects from the
 * publisher's OWN PDS (§16 explicit prune; roadmap item 9's opt-in action). Resolves each version's
 * manifest blob CIDs first so `planKeepLastPrune` (→ `planRetentionPrune`) reports releasable blobs
 * faithfully, then runs the tested `prune()` executor — which re-checks `site.current` and refuses to
 * delete the active version under a concurrent revert. Non-recoverable: confirmed before executing.
 */
async function applyRetention(site: SiteEntry): Promise<void> {
  if (agent?.did === undefined) return;
  const keep = clampKeep(ui.retentionKeep.valueAsNumber);
  const currentUri = currentUriOf(site);
  const previewPlan = previewKeepLast(retentionVersions(), keep, currentUri);
  if (previewPlan.prune.length === 0) return;
  if (!confirm(`${retentionSummary(previewPlan)} Deleted versions can't be recovered. Continue?`)) return;

  ui.retentionApply.disabled = true;
  setStatus("Deleting older versions…", false);
  try {
    // Resolve blob CIDs for every version so the plan's releasable-blob report is accurate (the deletes
    // themselves need only the deployment + manifest URIs already in the panel).
    const withBlobs: RetentionVersion[] = await Promise.all(
      panelVersions.map(async (v) => ({
        deploymentUri: v.uri,
        manifestUri: v.manifestUri,
        createdAt: v.createdAt,
        blobCids: await versionBlobCids(v.manifestUri),
      })),
    );
    const plan = planKeepLastPrune(withBlobs, keep, currentUri);
    const result = await prune({ client: atpClient(agent), siteRkey: site.rkey, plan });
    const n = result.deletedDeployments.length;
    setStatus(`Deleted ${n} older version${n === 1 ? "" : "s"}.`, false);
    void loadVersions(site); // re-paint from the now-pruned repo
  } catch (err) {
    setStatus(`Delete failed: ${errMessage(err)}`, true);
    renderRetention(site); // restore the Apply button's enabled state
  }
}

/** Revert `site` to an earlier deployment by re-pointing its `app.szzt.site` `current` strong ref at
 *  that deployment — a cheap single-record update (no rebuild). Non-destructive: newer deployments
 *  remain and can be re-selected. Delegates to the library `rollback()` (§8) so the repoint is guarded
 *  by both the site record's CID and the repo head, and refuses to swap `current` onto a target a
 *  concurrent prune/publish has moved or deleted (the H9 integrity hazard) — same guard, same
 *  client-held credentials, as the publish() path. */
async function revertTo(site: SiteEntry, version: VersionEntry): Promise<void> {
  if (agent === undefined || agent.did === undefined) return;
  if (!confirm(`Revert "${site.title}" to the version from ${formatWhen(version.createdAt)}?`)) return;
  setStatus("Reverting…", false);
  try {
    await rollback({
      client: atpClient(agent),
      siteRkey: site.rkey,
      target: { uri: version.uri, cid: version.cid },
    });
    site.currentDeploymentUri = version.uri;
    setStatus(`Reverted "${site.title}" to an earlier version.`, false);
    // Refresh the list (size/pointer) and re-paint the panel so the "current" marker moves.
    await loadSites();
    const refreshed = sites.find((s) => s.rkey === site.rkey) ?? site;
    if (updateTarget?.rkey === site.rkey) {
      updateTarget = refreshed;
      render();
    }
    void loadVersions(refreshed);
  } catch (err) {
    setStatus(`Revert failed: ${errMessage(err)}`, true);
  }
}

/** Write the status line + toggle its error styling. Used by the async site actions (delete/claim). */
function setStatus(text: string, isError: boolean): void {
  ui.status.textContent = text;
  ui.status.classList.toggle("err", isError);
}

/** Write the "Your sites" notice (migrateLegacySites' progress/result, loadSites' load failures) + toggle its error
 *  styling, and reveal it. Also re-derives `#sites-section`'s own visibility (sitesSectionVisible) since
 *  a freshly-revealed notice can be the only reason the section should be showing at all. */
function setSitesNotice(text: string, isError: boolean): void {
  ui.sitesNotice.textContent = text;
  ui.sitesNotice.classList.toggle("err", isError);
  ui.sitesNotice.classList.remove("hidden");
  ui.sitesSection.classList.toggle("hidden", !sitesSectionVisible());
}

/** Hide the "Your sites" notice again, re-deriving `#sites-section`'s visibility in case the notice was
 *  the only thing holding it open. */
function clearSitesNotice(): void {
  ui.sitesNotice.classList.add("hidden");
  ui.sitesSection.classList.toggle("hidden", !sitesSectionVisible());
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// Resolve the signed-in DID to its handle (via the account's PDS) so the UI shows `you.example.com`
// rather than a raw `did:plc:…`. Best-effort: on failure the DID stays shown.
async function loadIdentity(): Promise<void> {
  const actingAgent = agent;
  if (actingAgent?.did === undefined) return;
  const did = actingAgent.did;
  let resolvedHandle: string | undefined;
  try {
    const res = await actingAgent.com.atproto.repo.describeRepo({ repo: did });
    resolvedHandle = res.data.handle;
  } catch {
    resolvedHandle = undefined;
  }
  if (agent !== actingAgent) return;
  // Best-effort avatar from the public appview (unauthenticated); a failure just leaves it hidden.
  let resolvedAvatar: string | undefined;
  try {
    const res = await fetch(profileEndpointUrl(resolvedHandle ?? did));
    if (res.ok) resolvedAvatar = avatarFromProfile(await res.json());
  } catch {
    resolvedAvatar = undefined;
  }
  // Hand the resolved fields to the auth owner: it accretes them into the identity, persists the full
  // hint + last-account (so the next load's optimistic UI shows the handle/avatar, not the raw DID), and
  // renders. Passing undefined for a field that failed to resolve keeps any already-known value.
  if (agent !== actingAgent) return;
  authDispatch({ type: "IDENTITY_RESOLVED", handle: resolvedHandle, avatar: resolvedAvatar });
  renderSites(); // visit links depend on the resolved handle
}

// List the signed-in account's published `app.szzt.site` records (newest first). First page only
// (100) — a publisher with more sites than that is not a launch concern. Paints the loading row
// before awaiting so the section is visible for the whole round-trip, and settles it only if no
// newer load has since started (sitesRequestSeq — see its declaration).
async function loadSites(): Promise<void> {
  const actingAgent = agent;
  if (actingAgent?.did === undefined) return;
  const request = ++sitesRequestSeq;
  sitesLoading = true;
  renderSites();
  try {
    const res = await actingAgent.com.atproto.repo.listRecords({
      repo: actingAgent.did,
      collection: COLLECTION.site,
      limit: 100,
    });
    if (request !== sitesRequestSeq || agent !== actingAgent) return; // superseded/session ended
    sites = res.data.records
      .map((r) => {
        const entry: SiteEntry = { rkey: r.uri.split("/").pop() ?? "", title: siteTitle(r.value) };
        const depUri = currentDeploymentUri(r.value);
        if (depUri !== undefined) entry.currentDeploymentUri = depUri;
        return entry;
      })
      .sort((a, b) => (a.rkey < b.rkey ? 1 : -1)); // TID rkeys sort chronologically; newest first
    if (sitesLoadFailed) {
      sitesLoadFailed = false;
      clearSitesNotice(); // ours to clear — a successful load retires the error it reported
    }
  } catch (err) {
    if (request !== sitesRequestSeq || agent !== actingAgent) return;
    // A failed listing must never masquerade as an empty account. Keep whatever the list already
    // holds (staleness beats a false empty, same trade as the spinner) and report the failure in
    // the notice — which also holds the section open when there is no list to show at all.
    sitesLoadFailed = true;
    setSitesNotice(`Couldn't load your sites — ${errMessage(err)}. Reload the page or sign in again.`, true);
  }
  sitesLoading = false;
  renderSites();
  if (!sitesLoadFailed) {
    void hydrateSizes(); // fill in each site's size lazily; patches the list as results arrive
    if (ROUTE_COLLECTION && agent !== undefined) void loadSiteNames(request, agent);
  }
}

// Name-list failures must not hide a successfully loaded site list. This is a
// separate, read-only management listing; it is never browser-verification proof.
async function loadSiteNames(request: number, actingAgent: Agent): Promise<void> {
  const did = actingAgent.did;
  if (did === undefined) return;
  try {
    const records: { uri: string; value: unknown }[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < 10; page++) {
      const res = await actingAgent.com.atproto.repo.listRecords({ repo: did, collection: ROUTE_COLLECTION, limit: 100, ...(cursor ? { cursor } : {}) });
      if (request !== sitesRequestSeq || agent !== actingAgent) return;
      records.push(...res.data.records);
      cursor = res.data.cursor;
      if (!cursor) break;
    }
    const grouped = siteNames(records, did, ROUTE_COLLECTION);
    for (const site of sites) site.names = grouped.get(site.rkey) ?? [];
    renderSites();
    if (cursor) setStatus("Only the first 1,000 names are shown; the site list is unaffected.", true);
  } catch (err) {
    if (request === sitesRequestSeq && agent === actingAgent) setStatus(`Sites loaded, but couldn't load their names — ${errMessage(err)}`, true);
  }
}

/**
 * Migrate any records this account still holds under the retired `pub.mini.*` NSIDs onto the
 * current `app.szzt.*` ones (see @szzt/publish's migrate.ts). Run once per login rather than on a
 * timer: a legacy record only ever matters when its owner is next active, and login is the one
 * moment this client already has a live, authenticated agent to enumerate and write the repo with.
 * It is additive-only — `migrate()` only creates the new records and never touches the legacy
 * originals — so a login that is interrupted mid-migration leaves nothing worse than an unfinished
 * migration for the next login to pick back up. The repo is the done-signal, not a localStorage
 * flag: a flag lives on one device and would drift from the account's real state the moment the
 * same account logs in from a second browser, either re-attempting a finished migration or (worse)
 * skipping one a first device never got to. `migrate()` itself refuses to redo a completed site
 * (AlreadyMigratedError), so re-checking the repo on every login costs nothing when there is
 * nothing left to do.
 */
async function migrateLegacySites(): Promise<void> {
  const actingAgent = agent;
  if (actingAgent?.did === undefined) return;
  let repo: Awaited<ReturnType<typeof listLegacyRepo>>;
  try {
    repo = await listLegacyRepo(atpRecordLister(actingAgent));
  } catch {
    // Enumerating a repo with no legacy records at all is the overwhelmingly common case, and it
    // succeeds cleanly with empty lists rather than throwing — so a throw here means the listing
    // itself failed (network, auth), not worth a status line on every login; the next login retries.
    return;
  }
  if (agent !== actingAgent || repo.sites.length === 0) return; // stale or nothing to upgrade

  setSitesNotice(
    `Found ${repo.sites.length} older-format site${repo.sites.length === 1 ? "" : "s"} — upgrading…`,
    false,
  );

  let migratedCount = 0;
  const failures: string[] = [];
  // Legacy deployments no site could claim, plus ones the planner refused. They stay under the
  // retired NSIDs, where they are invisible to version history and can never be pruned — prune only
  // walks the current collections — so their manifests and blobs are pinned in the repo for good.
  // Reporting a count keeps that visible rather than silently lossy. A Set because the ambiguous
  // list is repo-wide: every site sees the same unclaimable deployments, so summing would multiply.
  const stranded = new Set<string>();
  for (const site of repo.sites) {
    if (agent !== actingAgent) return;
    // Sequential, not concurrent: every migrate() here is a swapCommit-guarded write against the
    // SAME repo, so running them in parallel would just have them retry against each other.
    try {
      // Opts into nearest-site attribution: stranding a version under the retired NSIDs is permanent
      // and unprunable, while a version filed under the wrong site's history is merely visible and
      // correctable — and a publisher's own repo, written by one client over time, is exactly the
      // case the nearest-TID heuristic is accurate for.
      const attribution = legacyDeploymentsForSite(repo, site.uri, {
        attributeUnclaimedToNearestSite: true,
      });
      const plan = await planMigration({ site, deployments: attribution.deployments });
      if (agent !== actingAgent) return;
      await migrate({ client: atpClient(actingAgent), plan });
      migratedCount++;
      for (const dep of attribution.ambiguous) stranded.add(dep.uri);
      for (const skip of plan.skipped) stranded.add(skip.uri);
    } catch (err) {
      if (agent !== actingAgent) return;
      if (err instanceof AlreadyMigratedError) continue; // already done — not a failure
      failures.push(errMessage(err));
    }
  }

  if (agent !== actingAgent) return;
  const strandedNote =
    stranded.size === 0
      ? ""
      : ` ${stranded.size} older version${stranded.size === 1 ? "" : "s"} could not be attributed to a site and stayed behind.`;

  if (migratedCount > 0) {
    await loadSites(); // the newly created app.szzt.site records now belong in the list
    if (agent !== actingAgent) return;
    setSitesNotice(
      `Upgraded ${migratedCount} older-format site${migratedCount === 1 ? "" : "s"} to the current format.${strandedNote}`,
      false,
    );
  } else if (failures.length > 0) {
    setSitesNotice(
      `Could not upgrade ${failures.length} older-format site${failures.length === 1 ? "" : "s"}: ${failures[0]}`,
      true,
    );
  } else {
    // Every legacy site was already migrated and nothing failed — clear the notice rather than leaving
    // the "upgrading…" line stuck. Not setStatus/render(): the migration reports through its own notice
    // in "Your sites" now, not the publish flow's #status line, so a migration finishing mid-publish
    // can never clobber a publish status.
    clearSitesNotice();
  }
}

/** A site record's display title, falling back to a generic label when it carries none. */
function siteTitle(value: unknown): string {
  if (value !== null && typeof value === "object" && "title" in value) {
    const t = (value as { title: unknown }).title;
    if (typeof t === "string" && t.length > 0) return t;
  }
  return "untitled site";
}

/** A site record's `current.uri` (its active deployment AT-URI), or undefined if malformed. */
function currentDeploymentUri(value: unknown): string | undefined {
  const current = (value as { current?: unknown } | null)?.current;
  const uri = (current as { uri?: unknown } | undefined)?.uri;
  return typeof uri === "string" ? uri : undefined;
}

/** Read the `tile.cid` (manifest-record atproto CID) of a deployment by its AT-URI — the value the next
 *  version's `prev` chains to (§4.3). The strong-ref `cid` is a plain `format: cid` string in the record
 *  value. Best-effort: returns undefined if the record can't be read or carries no tile cid. */
async function currentManifestCid(deploymentUri: string): Promise<string | undefined> {
  if (agent?.did === undefined) return undefined;
  const rkey = deploymentUri.split("/").pop();
  if (rkey === undefined || rkey === "") return undefined;
  try {
    const res = await agent.com.atproto.repo.getRecord({
      repo: agent.did,
      collection: COLLECTION.deployment,
      rkey,
    });
    const cid = (res.data.value as { tile?: { cid?: unknown } } | null)?.tile?.cid;
    return typeof cid === "string" ? cid : undefined;
  } catch {
    return undefined;
  }
}

async function main(): Promise<void> {
  console.info("[szzt] boot: main() started");
  // Optimistic restore: if a prior session was hinted in localStorage, INIT paints the signed-in shell
  // (phase "restoring") right away so a returning user doesn't see the signed-out form (and start typing
  // their handle) during the OAuth restore lag. Reconciled below once oauth.init() resolves.
  authDispatch({ type: "INIT" });
  // Load any saved mirror configs + prefill their forms (the sections stay hidden until signed in).
  r2Config = readR2Config();
  renderR2();
  ipfsConfig = readIpfsConfig();
  renderIpfs();

  const sessions = sessionLifecycle({
    currentDid: () => agent?.did ?? (auth.phase === "restoring" ? auth.identity.did : undefined),
    onLost: (did) => authDispatch({ type: "SESSION_LOST", did }),
  });
  const finishRestore = sessions.beginRestore();
  const oauth = await BrowserOAuthClient.load({
    clientId: CLIENT_ID,
    handleResolver: HANDLE_RESOLVER,
    onDelete: (sub, cause) => {
      // Log identity/error class only, never token sets, request objects or credentials.
      console.warn("[szzt] auth: session deleted", sub, cause instanceof Error ? cause.name : "unknown cause");
      sessions.onDelete(sub);
    },
  });
  console.info("[szzt] boot: oauth client loaded");

  // Restores a persisted session, or completes the redirect callback if we just returned from the PDS.
  // init() THROWS (not returns undefined) when a stored session's refresh token has expired or been
  // revoked at the PDS ("invalid_grant" / TokenRefreshError: "Session expired"). Swallow it and fall
  // back to signed-out: an uncaught rejection here aborts the rest of main(), leaving the sign-in
  // form's submit handler (and every listener below) unattached — which reads as "login is broken".
  // Restore WITHOUT forcing a token refresh (init(false) → allowStale). At boot this returns the
  // stored token set as-is, so two tabs starting together (a browser session-restore reopening every
  // tab at once) can never race on the single-use refresh-token rotation — one rotation invalidates
  // the other tab's grant (invalid_grant), and the cross-tab Web Lock that serializes refreshes has
  // no timeout on the WAIT, so a holder wedged mid-refresh silently queues every other tab's restore
  // forever. An expired token is not skipped: the session refreshes it lazily on the first API call
  // (fetchHandler refreshes on demand and retries on 401), where a failure surfaces as a normal
  // request error — shown by loadSites' notice — instead of a dead signed-in shell.
  let restored: Awaited<ReturnType<typeof oauth.init>>;
  try {
    restored = await oauth.init(false);
  } catch (err) {
    console.warn("[szzt] boot: session restore failed:", err instanceof Error ? err.message : err);
    restored = undefined;
  }
  if (!finishRestore(restored?.session.sub)) restored = undefined;
  console.info(
    restored !== undefined
      ? "[szzt] boot: session restored"
      : "[szzt] boot: no stored session (signed out)",
  );
  if (restored !== undefined) {
    // RESTORED assigns `agent` + moves phase→signedIn (the sole path in). Only then kick the follow-up
    // loads — they read the live `agent` and feed IDENTITY_RESOLVED back through the auth owner.
    authDispatch({ type: "RESTORED", session: restored.session });
    if (agent?.did !== undefined) {
      void loadIdentity();
      void loadSites();
      void migrateLegacySites();
    }
  } else {
    // No session (or a stale/expired hint) — drop any hint and fall back to signed-out, keeping lastAccount.
    authDispatch({ type: "RESTORE_EMPTY" });
  }

  // Start the OAuth redirect for a handle or PDS URL. Navigates away to the PDS authorization page; on
  // return, oauth.init() (above) picks up the session. Shared by the form submit, typeahead pick, and
  // "Continue as" card. When a full `identity` (with a DID) is supplied — the card path — we stash it as
  // a hint FIRST (via SIGN_IN_INTENT), so the OAuth return restores through the optimistic path with no
  // signed-out/DID flash. A typed handle / typeahead pick has no DID, so it stashes nothing and shows a
  // brief resolving state on that one login (acceptable — see auth-fsm-spec §3).
  const signIn = (identifier: string, identity?: Identity): void => {
    const value = identifier.trim();
    if (value === "") {
      ui.identifier.focus();
      return;
    }
    if (identity?.did !== undefined) authDispatch({ type: "SIGN_IN_INTENT", identity });
    void oauth.signIn(value);
  };

  // A <form> submit so <enter> in the handle field signs in (not just a button click). Typed handle → no
  // DID → no hint stashed.
  ui.signinForm.addEventListener("submit", (e) => {
    e.preventDefault();
    signIn(ui.identifier.value);
  });

  // One-click re-login with the remembered account. Passes the FULL identity (did/handle/avatar) so the
  // hint is stashed before navigating — the fix that makes this return paint through the restoring path.
  ui.continueAs.addEventListener("click", () => {
    const last = auth.lastAccount;
    if (last?.did !== undefined) signIn(last.handle ?? last.did, last);
  });

  // Forget the remembered account ("not me" / switch account): drop the persistent record so the
  // one-click card no longer shows, leaving just the sign-in form.
  ui.forgetAccount.addEventListener("click", () => {
    authDispatch({ type: "FORGET_ACCOUNT" });
  });

  // Live handle suggestions from the public appview; picking one signs in with that handle.
  attachHandleTypeahead({ input: ui.identifier, list: ui.suggestions }, signIn);

  ui.signout.addEventListener("click", async () => {
    const did = agent?.did;
    // Always end locally first: a missing/deleted session or failed revocation
    // must never strand the user in a signed-in shell with a dead Sign out button.
    authDispatch({ type: "SIGN_OUT" });
    const request = sitesRequestSeq;
    if (did !== undefined) {
      try { await oauth.revoke(did); }
      catch {
        console.warn("[szzt] auth: signed out locally; server revocation could not be confirmed");
        if (request === sitesRequestSeq && agent === undefined) {
          setStatus("Signed out here. Server revocation could not be confirmed; you can also revoke access in your PDS account settings.", true);
        }
      }
    }
  });

  ui.cancelUpdate.addEventListener("click", cancelUpdate);
  // Live-preview the keep-last-N retention plan as the count changes; apply it on demand. The control
  // only ever acts on `updateTarget` (the site whose panel is open), which loadVersions() has painted.
  ui.retentionKeep.addEventListener("input", () => {
    if (updateTarget !== undefined) renderRetention(updateTarget);
  });
  ui.retentionApply.addEventListener("click", () => {
    if (updateTarget !== undefined) void applyRetention(updateTarget);
  });
  ui.clearFiles.addEventListener("click", clearStagedBuild);

  ui.drop.addEventListener("click", () => ui.file.click());
  ui.file.addEventListener("change", async () => {
    if (ui.file.files === null) return;
    await onFiles(await filesFromFileList(ui.file.files));
  });
  ui.drop.addEventListener("dragover", (e) => {
    e.preventDefault();
    ui.drop.classList.add("hot");
  });
  ui.drop.addEventListener("dragleave", () => ui.drop.classList.remove("hot"));
  ui.drop.addEventListener("drop", async (e) => {
    e.preventDefault();
    ui.drop.classList.remove("hot");
    if (e.dataTransfer === null) return;
    await onFiles(await filesFromDataTransfer(e.dataTransfer));
  });

  // The §14 preview gate: when the sandboxed iframe finishes rendering the built site (its `srcdoc`
  // was set by mountPreview), promote the flow `ready` -> `previewed`, which enables Publish. Guarded
  // on a non-empty srcdoc so the initial about:blank load (and the clear-on-reset) don't count; even
  // if one slipped through, reduce() no-ops PREVIEW_OK from any state but `ready`.
  ui.previewFrame.addEventListener("load", () => {
    if (ui.previewFrame.srcdoc !== "") dispatch({ type: "PREVIEW_OK" });
  });

  ui.publish.addEventListener("click", () => void doPublish());
  ui.r2Save.addEventListener("click", saveR2);
  ui.r2Clear.addEventListener("click", clearR2);
  ui.ipfsSave.addEventListener("click", saveIpfs);
  ui.ipfsClear.addEventListener("click", clearIpfs);

  render();
  renderSites();
}

void main();
